import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";

function buildPageRange(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}

export function Pagination({ page, totalPages, total, onPage }: PaginationProps) {
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
      <span>
        {page}/{totalPages} · {total}
      </span>
      <div className="flex items-center gap-2">
        <PagerButton disabled={page <= 1} onClick={() => onPage(page - 1)} icon="chevronLeft" />
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            {buildPageRange(page, totalPages).map((pageNum, i) =>
              pageNum === "..." ? (
                <span key={`ellipsis-${i}`} className="grid h-8 w-8 place-items-center text-xs text-muted-foreground/60">
                  …
                </span>
              ) : (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => onPage(pageNum as number)}
                  className={cn(
                    "grid h-8 w-8 place-items-center rounded-full text-xs font-semibold transition-colors",
                    pageNum === page ? "bg-brand/10 text-brand" : "text-muted-foreground hover:bg-muted/20 hover:text-foreground",
                  )}
                >
                  {pageNum}
                </button>
              ),
            )}
          </div>
        )}
        <PagerButton disabled={page >= totalPages} onClick={() => onPage(page + 1)} icon="chevronRight" />
      </div>
    </div>
  );
}

function PagerButton({ disabled, onClick, icon }: { disabled: boolean; onClick: () => void; icon: "chevronLeft" | "chevronRight" }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-full border border-border/40 text-muted-foreground transition-colors hover:border-border/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
    >
      <Icon name={icon} className="h-4 w-4" />
    </button>
  );
}
